import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAgent } from '../../contexts/AgentContext';
import api from '../../api';
import {
  GetCompleteProDocResponse,
  ProDocItem as ProDocItemType,
  ProDocReply,
  GetProDocRepliesResponse,
  AddProDocReplyRequest,
  AddProDocReplyResponse,
  AgentEvents,
  ProDocUpdatedEvent,
  ProSheetUpdatedEvent,
  GetProSheetResponse,
  ProSheetData,
  UpdateProSheetRequest,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import ProDocItemWithReplies from './ProDocItemWithReplies';
import ProDocItem from './ProDocItem';
import ErrorState from '../common/ErrorState';
import ProDocSheetDisplay from './ProDocSheetDisplay';
import ProDocItemErrorBoundary from './ProDocItemErrorBoundary';
import ReplyItem from './ReplyItem';
import ReplyForm from './ReplyForm';
import SidewaysLoading from './sidebar/SidewaysLoading';

import './docs-dashboard.scss';
import ProDocCommentButton from './ProDocCommentButton';
import ProDocDownloadButton from './ProDocDownloadButton';

interface ProDocDisplayProps {
  proDocId: string;
  onBack?: () => void;
}

type LoadingState = 'idle' | 'loading' | 'success' | 'error';

/**
 * Format reply timestamp in a friendly way
 */
const formatReplyTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  // Less than 1 minute ago
  if (diffMins < 1) return 'just now';

  // Less than 60 minutes ago
  if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;

  // Less than 24 hours ago
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24)
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  // Today
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  if (dateOnly.getTime() === today.getTime()) {
    return `at ${date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  // Yesterday
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateOnly.getTime() === yesterday.getTime()) {
    return `yesterday at ${date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  // Older - show date and time
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `on ${day}/${month}/${year} at ${time}`;
};

const ProDocDisplay: React.FC<ProDocDisplayProps> = ({ proDocId, onBack }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { agents } = useAgent();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pusherChannel = usePusher();

  const [proDoc, setProDoc] = useState<
    GetCompleteProDocResponse['proDoc'] | null
  >(null);
  const [items, setItems] = useState<ProDocItemType[]>([]);
  const [sheetData, setSheetData] = useState<ProSheetData | null>(null);
  const [loading, setLoading] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  // Find the agent for this ProDoc to get predominant color
  const agent = proDoc?.agent_id
    ? agents.find((a) => a.id === proDoc.agent_id)
    : null;

  // Section navigation state
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);

  // Replies panel state
  const [selectedItemIdForReplies, setSelectedItemIdForReplies] = useState<
    string | null
  >(null);
  const [isProDocRepliesOpen, setIsProDocRepliesOpen] = useState(false);
  const [replies, setReplies] = useState<ProDocReply[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [repliesError, setRepliesError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSavingReply, setIsSavingReply] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch ProDoc data
  const fetchProDoc = useCallback(async () => {
    setLoading('loading');
    setError(null);
    setIsAccessDenied(false);

    try {
      const token = await getAccessTokenSilently();

      // First, get the ProDoc metadata to check type
      const metaResponse = await api.get<GetCompleteProDocResponse>(
        `/pro-docs/${proDocId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!metaResponse.data.success) {
        setLoading('error');
        setError(metaResponse.data.error || 'Failed to load ProDoc');
        return;
      }

      setProDoc(metaResponse.data.proDoc);

      // If it's a sheet, fetch sheet data instead of items
      if (metaResponse.data.proDoc.type === 'sheet') {
        const sheetResponse = await api.get<GetProSheetResponse>(
          `/pro-docs/${proDocId}/sheet`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (sheetResponse.data.success) {
          setSheetData(sheetResponse.data.data || null);
          setLoading('success');
        } else {
          setLoading('error');
          setError(sheetResponse.data.error || 'Failed to load ProSheet');
        }
      } else {
        // Regular ProDoc with items
        setItems(metaResponse.data.items);
        setLoading('success');
      }
    } catch (err: any) {
      console.error('Error fetching ProDoc:', err);
      setLoading('error');

      // Check if this is an access denied error (403)
      if (err?.response?.status === 403) {
        setIsAccessDenied(true);
        setError('Access denied');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load ProDoc');
      }
    }
  }, [proDocId, getAccessTokenSilently]);

  // Initial load
  useEffect(() => {
    fetchProDoc();
  }, [fetchProDoc]);

  // Fetch only sheet data without triggering full re-render
  const fetchSheetDataOnly = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetProSheetResponse>(
        `/pro-docs/${proDocId}/sheet`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.data) {
        // Only update sheet data state, not the entire proDoc
        setSheetData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching sheet data:', err);
    }
  }, [proDocId, getAccessTokenSilently]);

  // Handle checkbox update
  const handleCheckboxUpdate = (itemId: string, checked: boolean) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, checked } : item,
      ),
    );
  };

  // Handle comment button click for items
  const handleCommentClick = (itemId: string) => {
    setIsProDocRepliesOpen(false);
    setSelectedItemIdForReplies(itemId);
  };

  // Handle comment button click for ProDoc
  const handleProDocCommentClick = () => {
    setSelectedItemIdForReplies(null);
    setIsProDocRepliesOpen(!isProDocRepliesOpen);
  };

  // Fetch replies for the selected item or ProDoc
  const fetchReplies = useCallback(async () => {
    if (!selectedItemIdForReplies && !isProDocRepliesOpen) return;

    setRepliesLoading(true);
    setRepliesError(null);

    try {
      const token = await getAccessTokenSilently();

      // Fetch item replies or ProDoc replies
      const endpoint = selectedItemIdForReplies
        ? `/pro-docs/items/${selectedItemIdForReplies}/replies`
        : `/pro-docs/${proDocId}/replies`;

      const response = await api.get<GetProDocRepliesResponse>(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.replies) {
        setReplies(response.data.replies);
      } else {
        setRepliesError(response.data.error || 'Failed to load replies');
      }
    } catch (err) {
      console.error('Error fetching replies:', err);
      setRepliesError(
        err instanceof Error ? err.message : 'Failed to load replies',
      );
    } finally {
      setRepliesLoading(false);
    }
  }, [
    selectedItemIdForReplies,
    isProDocRepliesOpen,
    proDocId,
    getAccessTokenSilently,
  ]);

  // Load replies when selected item or ProDoc changes
  useEffect(() => {
    if (selectedItemIdForReplies || isProDocRepliesOpen) {
      fetchReplies();
    } else {
      setReplies([]);
      setReplyText('');
    }
  }, [selectedItemIdForReplies, isProDocRepliesOpen, fetchReplies]);

  // Handle saving a new reply
  const handleSaveReply = useCallback(async () => {
    if (
      !replyText.trim() ||
      (!selectedItemIdForReplies && !isProDocRepliesOpen)
    )
      return;

    setIsSavingReply(true);
    try {
      const token = await getAccessTokenSilently();

      // Determine endpoint and request body based on context
      const endpoint = selectedItemIdForReplies
        ? `/pro-docs/items/${selectedItemIdForReplies}/replies`
        : `/pro-docs/${proDocId}/replies`;

      const requestBody = selectedItemIdForReplies
        ? {
            proDocItemId: selectedItemIdForReplies,
            contents: {
              type: 'text',
              value: replyText.trim(),
            },
          }
        : {
            proDocId: proDocId,
            contents: {
              type: 'text',
              value: replyText.trim(),
            },
          };

      const response = await api.post<AddProDocReplyResponse>(
        endpoint,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        // Clear the form
        setReplyText('');

        // Reload replies to show the new one
        await fetchReplies();
      } else {
        alert(response.data.error || 'Failed to add reply');
      }
    } catch (err) {
      console.error('Error adding reply:', err);
      alert(err instanceof Error ? err.message : 'Failed to add reply');
    } finally {
      setIsSavingReply(false);
    }
  }, [
    replyText,
    selectedItemIdForReplies,
    isProDocRepliesOpen,
    proDocId,
    getAccessTokenSilently,
    fetchReplies,
  ]);

  const handleCancelReply = () => {
    setSelectedItemIdForReplies(null);
    setIsProDocRepliesOpen(false);
    setReplyText('');
  };

  // Handle ProDoc download
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/pro-doc-downloads/${proDocId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Determine file extension based on ProDoc type
      const extension = proDoc?.type === 'sheet' ? 'csv' : 'pdf';
      const filename = `${proDoc?.title || 'download'}.${extension}`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading ProDoc:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Listen for ProDoc updates via Pusher
  useEffect(() => {
    if (!pusherChannel || !proDocId) return;

    const handleProDocUpdate = (data: ProDocUpdatedEvent) => {
      // Only refresh if the update is for this ProDoc
      if (data.proDocId === proDocId) {
        // Smooth scroll to top
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }

        // Refresh the ProDoc
        fetchProDoc();

        // Refresh replies if panel is open
        if (selectedItemIdForReplies || isProDocRepliesOpen) {
          fetchReplies();
        }
      }
    };

    const handleProSheetUpdate = (data: ProSheetUpdatedEvent) => {
      // Only refresh if the update is for this ProSheet
      if (data.proDocId === proDocId && proDoc?.type === 'sheet') {
        // Fetch only sheet data, no full re-render
        fetchSheetDataOnly();

        // Refresh replies if panel is open
        if (selectedItemIdForReplies || isProDocRepliesOpen) {
          fetchReplies();
        }
      }
    };

    pusherChannel.bind(AgentEvents.PRO_DOC_UPDATED, handleProDocUpdate);
    pusherChannel.bind(AgentEvents.PRO_SHEET_UPDATED, handleProSheetUpdate);

    return () => {
      pusherChannel.unbind(AgentEvents.PRO_DOC_UPDATED, handleProDocUpdate);
      pusherChannel.unbind(AgentEvents.PRO_SHEET_UPDATED, handleProSheetUpdate);
    };
  }, [
    pusherChannel,
    proDocId,
    proDoc?.type,
    fetchProDoc,
    fetchSheetDataOnly,
    selectedItemIdForReplies,
    isProDocRepliesOpen,
    fetchReplies,
  ]);

  // Track last local update timestamp to prevent refetch loops
  const lastLocalUpdateRef = useRef<number>(0);

  // Handle sheet data changes
  const handleSheetDataChange = useCallback(
    async (newData: ProSheetData) => {
      try {
        // Mark this as a local update
        lastLocalUpdateRef.current = Date.now();

        const token = await getAccessTokenSilently();
        await api.patch<UpdateProSheetRequest>(
          `/pro-docs/${proDocId}/sheet`,
          { data: newData },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        // Update parent state so other users' changes can be detected
        setSheetData(newData);
      } catch (err) {
        console.error('Error updating sheet:', err);
      }
    },
    [proDocId, getAccessTokenSilently],
  );

  // Helper function to check if text is surrounded by strikethrough markdown
  const isStrikethrough = (text: string | undefined | null): boolean => {
    if (!text) return false;
    const trimmed = text.trim();
    if (
      trimmed.startsWith('~~') &&
      trimmed.endsWith('~~') &&
      trimmed.length > 4
    ) {
      return true;
    }
    return false;
  };

  // Helper function to check if an item should be displayed last (strikethrough or checked)
  const shouldDisplayLast = (item: ProDocItemType): boolean => {
    // Check if checkbox is checked
    if (item.checkbox && item.checked) return true;

    // Check caption
    if (isStrikethrough(item.caption)) return true;

    // Check text component value
    if (item.component?.type === 'Text' && item.component.props?.value) {
      return isStrikethrough(item.component.props.value as string);
    }

    return false;
  };

  // Sort items: non-strikethrough first, strikethrough last, maintaining order within each group
  const sortedItems = [...items].sort((a, b) => {
    const aIsLast = shouldDisplayLast(a);
    const bIsLast = shouldDisplayLast(b);

    if (aIsLast && !bIsLast) return 1;
    if (!aIsLast && bIsLast) return -1;

    // If both are in the same category, maintain original order
    return a.order - b.order;
  });

  // Split items into sections based on h1 headings
  interface Section {
    title: string;
    items: ProDocItemType[];
  }

  const sections: Section[] = [];
  let buildingSection: Section | null = null;

  for (const item of sortedItems) {
    // Check if this is an h1 heading
    const level = item.component?.props?.level;
    const isH1Heading =
      item.component?.type === 'Heading' &&
      (level === '1' || level === 'h1' || level === 1);

    if (isH1Heading) {
      // Save previous section if exists
      if (buildingSection) {
        sections.push(buildingSection);
      }

      // Start new section
      const headingValue = item.component?.props?.value as string;
      buildingSection = {
        title: headingValue || 'Untitled Section',
        items: [item], // Include the heading in the section
      };
    } else {
      // If no section started yet, create a default first section
      if (!buildingSection) {
        buildingSection = {
          title: 'Document',
          items: [],
        };
      }
      buildingSection.items.push(item);
    }
  }

  // Add the last section
  if (buildingSection) {
    sections.push(buildingSection);
  }

  // If no sections were created (empty items), create a default empty section
  if (sections.length === 0) {
    sections.push({
      title: 'Document',
      items: [],
    });
  }

  // Reset section index if it becomes invalid (e.g., after ProDoc update removes sections)
  useEffect(() => {
    if (selectedSectionIndex >= sections.length && sections.length > 0) {
      setSelectedSectionIndex(0);
      setSelectedItemIdForReplies(null);
      setIsProDocRepliesOpen(false);
      setReplyText('');
    }
  }, [sections.length, selectedSectionIndex]);

  // Close comment tray if the selected item no longer exists
  useEffect(() => {
    if (selectedItemIdForReplies) {
      const itemExists = items.some(
        (item) => item.id === selectedItemIdForReplies,
      );
      if (!itemExists) {
        setSelectedItemIdForReplies(null);
        setReplyText('');
      }
    }
  }, [items, selectedItemIdForReplies]);

  // Get the current section to display
  const currentSection = sections[selectedSectionIndex] || sections[0];

  // Render a single ProDoc item
  const renderProDocItem = (item: ProDocItemType) => {
    return (
      <ProDocItemErrorBoundary key={item.id} itemId={item.id}>
        <ProDocItemWithReplies
          item={item}
          agentPredominantColour={agent?.predominant_colour}
          onCommentClick={handleCommentClick}
          isRepliesExpanded={selectedItemIdForReplies === item.id}
        >
          <ProDocItem item={item} onCheckboxUpdate={handleCheckboxUpdate} />
        </ProDocItemWithReplies>
      </ProDocItemErrorBoundary>
    );
  };

  // Loading state
  if (loading === 'loading') {
    return (
      <div className='pro-doc-display'>
        <div className='pro-docs-loading'>
          <div className='ghost-band-one'>
            <div className='ghost'></div>
          </div>
          <div className='ghost-band-two'>
            <div className='ghost'></div>
            <div className='ghost'></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (loading === 'error') {
    return (
      <div className='pro-doc-display'>
        <div className='pro-docs-error'>
          {isAccessDenied ? (
            <ErrorState
              title="You don't have access to this document"
              subtitle='Ask the thread owner or document creator to share it with you'
            />
          ) : (
            <ErrorState
              title="I can't load this document"
              subtitle='Please refresh the page and try again'
            />
          )}
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div
      className={`pro-doc-display ${proDoc?.type === 'sheet' ? 'pro-doc-sheet' : ''}`}
      ref={scrollContainerRef}
    >
      <div className='content-inner'>
        {/* Render sheet or items based on type */}
        {proDoc?.type === 'sheet' ? (
          <div className='pro-doc-sheet-wrapper'>
            {proDoc && onBack && (
              <div className='pro-doc-header'>
                <h1 className='pro-doc-title'>{proDoc.title}</h1>
                <div className='download-and-comment'>
                  <ProDocDownloadButton
                    onClick={handleDownload}
                    processing={isDownloading}
                  />
                  <ProDocCommentButton
                    proDocId={proDocId}
                    isExpanded={isProDocRepliesOpen}
                    onClick={handleProDocCommentClick}
                  />
                </div>
              </div>
            )}

            <div className='pro-doc-sheet-container'>
              {sheetData && (
                <ProDocSheetDisplay
                  key={proDocId}
                  proDocId={proDocId}
                  initialData={sheetData}
                  readOnly={false}
                  onDataChange={handleSheetDataChange}
                />
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Render items in order */}
            {sortedItems.length > 0 ? (
              <div className='pro-doc-doc-wrapper'>
                <div className='pro-doc-doc-header'>
                  <h1>{proDoc?.title}</h1>
                  <ProDocDownloadButton
                    onClick={handleDownload}
                    processing={isDownloading}
                  />
                </div>
                <div className='lower-wrapper'>
                  {/* Section navigation */}
                  {sections.length > 0 && (
                    <div className='section-titles'>
                      <h6>Sections</h6>
                      {sections.map((section, index) => (
                        <div
                          key={index}
                          className={`section-title ${index === selectedSectionIndex ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedSectionIndex(index);
                            setSelectedItemIdForReplies(null);
                            setIsProDocRepliesOpen(false);
                            setReplyText('');
                          }}
                        >
                          {section.title}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Current section items */}
                  <div className='pro-doc-items'>
                    {currentSection?.items.map(renderProDocItem)}
                  </div>
                </div>
              </div>
            ) : (
              <div className='pro-docs-empty'>
                <p>No items in this ProDoc yet</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Replies panel */}
      {(selectedItemIdForReplies || isProDocRepliesOpen) && (
        <div className='pro-doc-replies'>
          {repliesLoading && (
            <div className='replies-loading'>
              <SidewaysLoading />
            </div>
          )}

          {repliesError && (
            <div className='replies-error'>
              <ErrorState
                title='Unable to load replies'
                subtitle='Refresh and try again'
              />
            </div>
          )}

          {!repliesLoading && !repliesError && (
            <div className='replies-list'>
              {replies.map((reply: ProDocReply) => (
                <ReplyItem
                  key={reply.id}
                  reply={reply}
                  formatReplyTime={formatReplyTime}
                  agentPredominantColour={agent?.predominant_colour}
                  smallWidth={true}
                />
              ))}
              <ReplyForm
                replyText={replyText}
                onReplyTextChange={setReplyText}
                onSave={handleSaveReply}
                onCancel={handleCancelReply}
                isSaving={isSavingReply}
                smallWidth={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProDocDisplay;
